/**
 * SubscriptionWizard — Multi-step subscription agreement form.
 *
 * Collects all data required for SEC Reg D/506(b)/506(c) subscription agreements:
 *   1. Vehicle type (individual / entity / trust / IRA)
 *   2. Title holding option
 *   3. Accredited investor basis
 *   4. Risk acknowledgments
 *   5. Investor information
 *   6. Bad actor questionnaire (Rule 506 disqualification)
 *   7. Community property waiver (if applicable)
 *   8. Review
 *
 * Emits SubscriptionData to the parent flow on completion.
 */

import { useState, useCallback } from 'react';
import {
  ArrowLeft, Building2, Users, Landmark, PiggyBank, ChevronRight, Check,
  Shield, AlertTriangle, User, FileText, CheckCircle2,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import type { SubscriptionWizardProps, SubscriptionStep, SubscriptionData } from './types';
import {
  VEHICLE_TYPES, TITLE_OPTIONS, ACCREDITED_OPTIONS, BAD_ACTOR_QUESTIONS,
  RISK_ACKNOWLEDGMENTS, COMMUNITY_PROPERTY_STATES, VehicleType,
} from './constants';

// ── Step list (order matters) ───────────────────────────────────────────
const STEPS: SubscriptionStep[] = [
  'vehicle', 'title', 'accredited', 'risks', 'investorInfo', 'badActor', 'communityProperty', 'subscriptionReview',
];

function stepLabel(s: SubscriptionStep): string {
  const m: Record<SubscriptionStep, string> = {
    vehicle: 'Investment Vehicle',
    title: 'Title Holding',
    accredited: 'Accredited Status',
    risks: 'Risk Acknowledgments',
    investorInfo: 'Investor Information',
    badActor: 'Disqualification Check',
    communityProperty: 'Community Property',
    subscriptionReview: 'Review & Confirm',
  };
  return m[s];
}

const VEHICLE_ICONS: Record<string, React.ReactNode> = {
  individual: <User className="w-5 h-5" />,
  entity: <Building2 className="w-5 h-5" />,
  trust: <Landmark className="w-5 h-5" />,
  ira: <PiggyBank className="w-5 h-5" />,
};

function formatCurrency(n: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
}

// ═══════════════════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════════════════

export function SubscriptionWizard({ onComplete, onBack, investmentAmount, dealTitle }: SubscriptionWizardProps) {
  const [step, setStep] = useState<SubscriptionStep>('vehicle');

  // ── Form state ─────────────────────────────────────────────────────────
  const [vehicleType, setVehicleType] = useState<VehicleType | null>(null);
  const [titleOption, setTitleOption] = useState<string>('');
  const [titleDetails, setTitleDetails] = useState<Record<string, string>>({});
  const [accreditedBases, setAccreditedBases] = useState<string[]>([]);
  const [accreditedVerification, setAccreditedVerification] = useState('');
  const [riskAcks, setRiskAcks] = useState<Record<string, boolean>>({});
  const [investorInfo, setInvestorInfo] = useState({
    fullName: '', email: '', phone: '', address: '', city: '', state: '', zip: '',
    ssn_last4: '', dob: '', entityName: '', ein: '', entityState: '',
  });
  const [badActorAnswers, setBadActorAnswers] = useState<Record<string, boolean>>({});
  const [communityPropertyWaiver, setCommunityPropertyWaiver] = useState(false);
  const [communityPropertyState, setCommunityPropertyState] = useState('');

  // ── Navigation ─────────────────────────────────────────────────────────
  const currentIdx = STEPS.indexOf(step);
  const progressPercent = ((currentIdx + 1) / STEPS.length) * 100;

  // Skip community property step unless title is community_property variant and state is community property state
  const getNextStep = useCallback((current: SubscriptionStep): SubscriptionStep | null => {
    const idx = STEPS.indexOf(current);
    if (idx >= STEPS.length - 1) return null;
    const next = STEPS[idx + 1];
    if (next === 'communityProperty') {
      const needsCommunity = titleOption?.includes('community') ||
        COMMUNITY_PROPERTY_STATES.includes(investorInfo.state);
      if (!needsCommunity) return STEPS[idx + 2] || null;
    }
    return next;
  }, [titleOption, investorInfo.state]);

  const getPrevStep = useCallback((current: SubscriptionStep): SubscriptionStep | null => {
    const idx = STEPS.indexOf(current);
    if (idx <= 0) return null;
    const prev = STEPS[idx - 1];
    if (prev === 'communityProperty') {
      const needsCommunity = titleOption?.includes('community') ||
        COMMUNITY_PROPERTY_STATES.includes(investorInfo.state);
      if (!needsCommunity) return STEPS[idx - 2] || null;
    }
    return prev;
  }, [titleOption, investorInfo.state]);

  const goNext = useCallback(() => {
    const next = getNextStep(step);
    if (next) setStep(next);
  }, [step, getNextStep]);

  const goBackStep = useCallback(() => {
    const prev = getPrevStep(step);
    if (prev) setStep(prev);
    else onBack();
  }, [step, getPrevStep, onBack]);

  // ── Step validation ────────────────────────────────────────────────────
  const canContinue = useCallback((): boolean => {
    switch (step) {
      case 'vehicle': return vehicleType !== null;
      case 'title': return titleOption !== '';
      case 'accredited': return accreditedBases.length > 0;
      case 'risks': return RISK_ACKNOWLEDGMENTS.every(r => riskAcks[r.id]);
      case 'investorInfo': {
        const { fullName, email, phone, address, city, state, zip } = investorInfo;
        const baseOk = !!(fullName && email && phone && address && city && state && zip);
        if (vehicleType === 'entity') return baseOk && !!investorInfo.entityName;
        return baseOk;
      }
      case 'badActor': return BAD_ACTOR_QUESTIONS.every(q => badActorAnswers[q.id] !== undefined);
      case 'communityProperty': return communityPropertyWaiver;
      case 'subscriptionReview': return true;
      default: return false;
    }
  }, [step, vehicleType, titleOption, accreditedBases, riskAcks, investorInfo, badActorAnswers, communityPropertyWaiver]);

  // ── Submit ─────────────────────────────────────────────────────────────
  const handleComplete = useCallback(() => {
    const data: SubscriptionData = {
      vehicleType: vehicleType!,
      titleOption,
      titleDetails,
      accreditedBases,
      accreditedVerification: accreditedVerification || undefined,
      riskAcknowledgments: riskAcks,
      investorInfo,
      badActorAnswers,
      communityPropertyWaiver: communityPropertyWaiver || undefined,
      communityPropertyState: communityPropertyState || undefined,
    };
    onComplete(data);
  }, [vehicleType, titleOption, titleDetails, accreditedBases, accreditedVerification, riskAcks, investorInfo, badActorAnswers, communityPropertyWaiver, communityPropertyState, onComplete]);

  // ── Helpers ────────────────────────────────────────────────────────────
  const toggleAccredited = (id: string) => {
    setAccreditedBases(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleRisk = (id: string) => {
    setRiskAcks(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const setBadActorAnswer = (id: string, val: boolean) => {
    setBadActorAnswers(prev => ({ ...prev, [id]: val }));
  };

  const updateInvestorInfo = (field: string, value: string) => {
    setInvestorInfo(prev => ({ ...prev, [field]: value }));
  };

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════

  return (
    <div className="flex flex-col flex-1">
      {/* Progress bar */}
      <div className="h-1 bg-secondary">
        <div className="h-full bg-primary transition-all duration-500 ease-out" style={{ width: `${progressPercent}%` }} />
      </div>

      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
        <button onClick={goBackStep} className="text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h2 className="text-lg font-semibold text-foreground">{stepLabel(step)}</h2>
          <p className="text-xs text-muted-foreground">Step {currentIdx + 1} of {STEPS.length}</p>
        </div>
      </div>

      {/* Step content */}
      <div className="flex-1 overflow-y-auto px-5 py-6 space-y-4">

        {/* ── VEHICLE ──────────────────────────────────────────────── */}
        {step === 'vehicle' && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">How will you hold this investment?</p>
            {VEHICLE_TYPES.map(v => (
              <button
                key={v.value}
                onClick={() => { setVehicleType(v.value); setTitleOption(''); }}
                className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center gap-4 ${
                  vehicleType === v.value ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
                }`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  vehicleType === v.value ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground'
                }`}>
                  {VEHICLE_ICONS[v.value]}
                </div>
                <div className="flex-1">
                  <div className="font-medium text-foreground">{v.label}</div>
                  <div className="text-sm text-muted-foreground">{v.desc}</div>
                </div>
                {vehicleType === v.value && <Check className="w-5 h-5 text-primary" />}
              </button>
            ))}
          </div>
        )}

        {/* ── TITLE ────────────────────────────────────────────────── */}
        {step === 'title' && vehicleType && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Select how title will be held.</p>
            {TITLE_OPTIONS[vehicleType].map(opt => (
              <button
                key={opt.value}
                onClick={() => setTitleOption(opt.value)}
                className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center justify-between ${
                  titleOption === opt.value ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
                }`}
              >
                <span className="font-medium text-foreground">{opt.label}</span>
                {titleOption === opt.value && <Check className="w-5 h-5 text-primary" />}
              </button>
            ))}

            {/* Entity-specific details */}
            {(vehicleType === 'entity' || vehicleType === 'trust') && titleOption && (
              <div className="mt-4 space-y-3 p-4 rounded-xl bg-secondary/50">
                <p className="text-sm font-medium text-foreground">
                  {vehicleType === 'entity' ? 'Entity Details' : 'Trust Details'}
                </p>
                <input
                  type="text"
                  placeholder={vehicleType === 'entity' ? 'Entity Name' : 'Trust Name'}
                  value={titleDetails.entityName || ''}
                  onChange={e => setTitleDetails(prev => ({ ...prev, entityName: e.target.value }))}
                  className="w-full p-3 rounded-lg border border-border bg-background text-foreground"
                />
                {vehicleType === 'entity' && (
                  <>
                    <input
                      type="text"
                      placeholder="EIN"
                      value={titleDetails.ein || ''}
                      onChange={e => setTitleDetails(prev => ({ ...prev, ein: e.target.value }))}
                      className="w-full p-3 rounded-lg border border-border bg-background text-foreground"
                    />
                    <input
                      type="text"
                      placeholder="State of Formation"
                      value={titleDetails.stateOfFormation || ''}
                      onChange={e => setTitleDetails(prev => ({ ...prev, stateOfFormation: e.target.value }))}
                      className="w-full p-3 rounded-lg border border-border bg-background text-foreground"
                    />
                  </>
                )}
              </div>
            )}

            {/* Joint owner details */}
            {titleOption?.includes('joint') && (
              <div className="mt-4 space-y-3 p-4 rounded-xl bg-secondary/50">
                <p className="text-sm font-medium text-foreground">Joint Owner Details</p>
                <input
                  type="text"
                  placeholder="Joint Owner Full Name"
                  value={titleDetails.jointName || ''}
                  onChange={e => setTitleDetails(prev => ({ ...prev, jointName: e.target.value }))}
                  className="w-full p-3 rounded-lg border border-border bg-background text-foreground"
                />
                <input
                  type="text"
                  placeholder="Joint Owner Address"
                  value={titleDetails.jointAddress || ''}
                  onChange={e => setTitleDetails(prev => ({ ...prev, jointAddress: e.target.value }))}
                  className="w-full p-3 rounded-lg border border-border bg-background text-foreground"
                />
              </div>
            )}
          </div>
        )}

        {/* ── ACCREDITED ──────────────────────────────────────────── */}
        {step === 'accredited' && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-5 h-5 text-primary" />
              <p className="text-sm text-muted-foreground">Select all that apply to demonstrate your accredited investor status.</p>
            </div>
            {ACCREDITED_OPTIONS.map(opt => (
              <label
                key={opt.id}
                className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                  accreditedBases.includes(opt.id) ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30'
                }`}
              >
                <input
                  type="checkbox"
                  checked={accreditedBases.includes(opt.id)}
                  onChange={() => toggleAccredited(opt.id)}
                  className="mt-1 h-4 w-4 rounded border-border text-primary"
                />
                <span className="text-sm text-foreground leading-snug">{opt.label}</span>
              </label>
            ))}

            <div className="mt-4">
              <label className="text-sm font-medium text-foreground block mb-1">
                Verification Method (optional)
              </label>
              <select
                value={accreditedVerification}
                onChange={e => setAccreditedVerification(e.target.value)}
                className="w-full p-3 rounded-lg border border-border bg-background text-foreground"
              >
                <option value="">Select verification method...</option>
                <option value="third_party_letter">Third-party verification letter</option>
                <option value="tax_returns">Tax returns</option>
                <option value="broker_statement">Broker/dealer statement</option>
                <option value="self_certification">Self-certification</option>
              </select>
            </div>
          </div>
        )}

        {/* ── RISKS ───────────────────────────────────────────────── */}
        {step === 'risks' && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
              <p className="text-sm text-amber-700 dark:text-amber-300">Please read and acknowledge each risk disclosure below.</p>
            </div>
            {RISK_ACKNOWLEDGMENTS.map(r => (
              <label
                key={r.id}
                className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  riskAcks[r.id] ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30'
                }`}
              >
                <input
                  type="checkbox"
                  checked={!!riskAcks[r.id]}
                  onChange={() => toggleRisk(r.id)}
                  className="mt-1 h-4 w-4 rounded border-border text-primary"
                />
                <span className="text-sm text-foreground leading-snug">{r.label}</span>
              </label>
            ))}
          </div>
        )}

        {/* ── INVESTOR INFO ───────────────────────────────────────── */}
        {step === 'investorInfo' && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Provide your investor information as it should appear on the subscription agreement.</p>

            <div>
              <label className="text-sm font-medium text-foreground block mb-1">Full Legal Name *</label>
              <input type="text" value={investorInfo.fullName} onChange={e => updateInvestorInfo('fullName', e.target.value)}
                className="w-full p-3 rounded-lg border border-border bg-background text-foreground" placeholder="John A. Smith" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-foreground block mb-1">Email *</label>
                <input type="email" value={investorInfo.email} onChange={e => updateInvestorInfo('email', e.target.value)}
                  className="w-full p-3 rounded-lg border border-border bg-background text-foreground" placeholder="john@example.com" />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground block mb-1">Phone *</label>
                <input type="tel" value={investorInfo.phone} onChange={e => updateInvestorInfo('phone', e.target.value)}
                  className="w-full p-3 rounded-lg border border-border bg-background text-foreground" placeholder="(555) 123-4567" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-foreground block mb-1">Street Address *</label>
              <input type="text" value={investorInfo.address} onChange={e => updateInvestorInfo('address', e.target.value)}
                className="w-full p-3 rounded-lg border border-border bg-background text-foreground" placeholder="123 Main St, Suite 200" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-sm font-medium text-foreground block mb-1">City *</label>
                <input type="text" value={investorInfo.city} onChange={e => updateInvestorInfo('city', e.target.value)}
                  className="w-full p-3 rounded-lg border border-border bg-background text-foreground" />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground block mb-1">State *</label>
                <input type="text" value={investorInfo.state} onChange={e => updateInvestorInfo('state', e.target.value)}
                  className="w-full p-3 rounded-lg border border-border bg-background text-foreground" maxLength={2} placeholder="TX" />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground block mb-1">ZIP *</label>
                <input type="text" value={investorInfo.zip} onChange={e => updateInvestorInfo('zip', e.target.value)}
                  className="w-full p-3 rounded-lg border border-border bg-background text-foreground" maxLength={10} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-foreground block mb-1">SSN (last 4)</label>
                <input type="text" value={investorInfo.ssn_last4} onChange={e => updateInvestorInfo('ssn_last4', e.target.value)}
                  className="w-full p-3 rounded-lg border border-border bg-background text-foreground" maxLength={4} placeholder="••••" />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground block mb-1">Date of Birth</label>
                <input type="date" value={investorInfo.dob} onChange={e => updateInvestorInfo('dob', e.target.value)}
                  className="w-full p-3 rounded-lg border border-border bg-background text-foreground" />
              </div>
            </div>

            {/* Entity-specific fields */}
            {(vehicleType === 'entity' || vehicleType === 'trust') && (
              <div className="space-y-3 pt-2 border-t border-border">
                <p className="text-sm font-medium text-foreground">Entity / Trust Information</p>
                <input type="text" value={investorInfo.entityName} onChange={e => updateInvestorInfo('entityName', e.target.value)}
                  placeholder="Entity / Trust Name" className="w-full p-3 rounded-lg border border-border bg-background text-foreground" />
                <div className="grid grid-cols-2 gap-3">
                  <input type="text" value={investorInfo.ein} onChange={e => updateInvestorInfo('ein', e.target.value)}
                    placeholder="EIN" className="w-full p-3 rounded-lg border border-border bg-background text-foreground" />
                  <input type="text" value={investorInfo.entityState} onChange={e => updateInvestorInfo('entityState', e.target.value)}
                    placeholder="State of Formation" className="w-full p-3 rounded-lg border border-border bg-background text-foreground" />
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── BAD ACTOR ───────────────────────────────────────────── */}
        {step === 'badActor' && (
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                <strong>SEC Rule 506 Disqualification Check.</strong> Answer each question honestly.
                A "Yes" answer does not automatically disqualify you.
              </p>
            </div>
            {BAD_ACTOR_QUESTIONS.map((q, i) => (
              <div key={q.id} className="p-4 rounded-xl border-2 border-border space-y-3">
                <p className="text-sm text-foreground leading-snug">
                  <span className="font-semibold text-muted-foreground mr-1">{i + 1}.</span>
                  {q.text}
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setBadActorAnswer(q.id, false)}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                      badActorAnswers[q.id] === false ? 'bg-emerald-500 text-white' : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
                    }`}
                  >
                    No
                  </button>
                  <button
                    onClick={() => setBadActorAnswer(q.id, true)}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                      badActorAnswers[q.id] === true ? 'bg-red-500 text-white' : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
                    }`}
                  >
                    Yes
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── COMMUNITY PROPERTY ──────────────────────────────────── */}
        {step === 'communityProperty' && (
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <p className="text-sm text-amber-700 dark:text-amber-300">
                Your situation may require community property disclosure.
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-foreground block mb-1">State of Residence</label>
              <select
                value={communityPropertyState}
                onChange={e => setCommunityPropertyState(e.target.value)}
                className="w-full p-3 rounded-lg border border-border bg-background text-foreground"
              >
                <option value="">Select state...</option>
                {COMMUNITY_PROPERTY_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <label className="flex items-start gap-3 p-4 rounded-xl border-2 border-border cursor-pointer">
              <input
                type="checkbox"
                checked={communityPropertyWaiver}
                onChange={e => setCommunityPropertyWaiver(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-border text-primary"
              />
              <span className="text-sm text-foreground leading-snug">
                I acknowledge that this investment may be community property and my spouse/registered domestic partner
                has been informed of and consents to this investment, or I certify that I have sole ownership of the funds being invested.
              </span>
            </label>
          </div>
        )}

        {/* ── REVIEW ──────────────────────────────────────────────── */}
        {step === 'subscriptionReview' && (
          <div className="space-y-4">
            {dealTitle && (
              <div className="text-center pb-2">
                <h3 className="text-lg font-bold text-foreground">{dealTitle}</h3>
                {investmentAmount !== undefined && (
                  <p className="text-2xl font-bold text-primary">{formatCurrency(investmentAmount)}</p>
                )}
              </div>
            )}

            <ReviewSection title="Investment Vehicle" icon={<Building2 className="w-4 h-4" />}>
              <p className="capitalize">{vehicleType?.replace('_', ' ')}</p>
              <p className="text-sm text-muted-foreground">{titleOption?.replace(/_/g, ' ')}</p>
              {Object.entries(titleDetails).filter(([, v]) => v).map(([k, v]) => (
                <p key={k} className="text-sm text-muted-foreground">{k}: {v}</p>
              ))}
            </ReviewSection>

            <ReviewSection title="Accredited Status" icon={<Shield className="w-4 h-4" />}>
              <ul className="text-sm space-y-1">
                {accreditedBases.map(id => {
                  const opt = ACCREDITED_OPTIONS.find(o => o.id === id);
                  return <li key={id} className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" /><span>{opt?.label}</span></li>;
                })}
              </ul>
              {accreditedVerification && <p className="text-sm text-muted-foreground mt-1">Verification: {accreditedVerification.replace(/_/g, ' ')}</p>}
            </ReviewSection>

            <ReviewSection title="Investor Information" icon={<User className="w-4 h-4" />}>
              <p className="font-medium">{investorInfo.fullName}</p>
              <p className="text-sm text-muted-foreground">{investorInfo.email} • {investorInfo.phone}</p>
              <p className="text-sm text-muted-foreground">{investorInfo.address}</p>
              <p className="text-sm text-muted-foreground">{investorInfo.city}, {investorInfo.state} {investorInfo.zip}</p>
              {investorInfo.entityName && <p className="text-sm text-muted-foreground mt-1">Entity: {investorInfo.entityName} ({investorInfo.entityState})</p>}
            </ReviewSection>

            <ReviewSection title="Bad Actor Check" icon={<FileText className="w-4 h-4" />}>
              {Object.values(badActorAnswers).every(v => !v) ? (
                <p className="text-sm text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> All clear — no disqualifying events</p>
              ) : (
                <p className="text-sm text-amber-600 flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> One or more questions answered "Yes" — review required</p>
              )}
            </ReviewSection>

            <ReviewSection title="Risk Acknowledgments" icon={<AlertTriangle className="w-4 h-4" />}>
              <p className="text-sm text-emerald-600 flex items-center gap-1">
                <CheckCircle2 className="w-4 h-4" /> All {RISK_ACKNOWLEDGMENTS.length} risks acknowledged
              </p>
            </ReviewSection>
          </div>
        )}
      </div>

      {/* Footer with navigation buttons */}
      <div className="px-5 py-4 border-t border-border flex gap-3">
        <Button variant="outline" onClick={goBackStep} className="flex-1">
          Back
        </Button>
        {step === 'subscriptionReview' ? (
          <Button onClick={handleComplete} className="flex-1">
            Confirm & Continue
          </Button>
        ) : (
          <Button onClick={goNext} disabled={!canContinue()} className="flex-1">
            Continue <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        )}
      </div>
    </div>
  );
}

// ── Review Section helper ────────────────────────────────────────────────
function ReviewSection({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <Card className="p-4 space-y-2">
      <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
        {icon}
        {title}
      </div>
      <div className="text-foreground">{children}</div>
    </Card>
  );
}

export default SubscriptionWizard;
